import { apiService } from '@/shared/services/apiServices';
import { AxiosError } from 'axios';
import { TASK_FORM_ERROR_MESSAGES } from '../errors/errorMessages';
import type {
  FormBuilderState,
  FormField,
  FormSection,
  CreateTaskFormResponse,
} from '../types';
import taskFormEndpoints from './taskFormEndpoints';

// ─── Payload builders ────────────────────────────────────────────────────────

function toApiId(uuid: string): string {
  return `f${uuid.replace(/-/g, '')}`;
}

function buildFieldPayload(field: FormField, order: number) {
  const apiType =
    field.type === 'number_range'
      ? 'number'
      : field.type === 'date_range'
        ? 'date'
        : field.type;

  const config: Record<string, unknown> = { label: field.label };

  if (
    (field.type === 'number' || field.type === 'number_range') &&
    field.unit
  ) {
    config['uom'] = field.unit;
  }
  if (field.type === 'price') {
    config['currency'] = 'PLN';
  }
  if (field.type === 'attachment') {
    if (field.acceptedFileTypes?.length) {
      config['acceptedFileTypes'] = field.acceptedFileTypes.map(
        (t) => `.${t.toLowerCase()}`
      );
    }
    if (field.maxFiles != null) {
      config['maxFiles'] = field.maxFiles;
    }
    if (field.maxFileSizeMb != null) {
      config['maxFileSizeMb'] = field.maxFileSizeMb;
    }
  }
  if (
    field.type === 'dropdown' ||
    field.type === 'radio' ||
    field.type === 'checkbox'
  ) {
    config['options'] = (field.options ?? []).map((opt) => ({
      label: opt.label,
      value: opt.value || opt.label.toLowerCase().replace(/\s+/g, '_'),
    }));
    if (field.type === 'dropdown') {
      config['multiSelect'] = field.multiSelect ?? false;
    }
  }

  const validationRules: Record<string, unknown> = {
    required: field.required,
  };

  if (field.type === 'text') {
    validationRules['maxLength'] = 200;
  }
  if (field.type === 'textarea') {
    validationRules['maxLength'] = 500;
  }
  if (field.type === 'number' || field.type === 'number_range') {
    if (field.min != null) {
      validationRules['min'] = field.min;
    }
    if (field.max != null) {
      validationRules['max'] = field.max;
    }
  }

  return {
    id: toApiId(field.id),
    type: apiType,
    config,
    validationRules,
    uiOptions: { order },
  };
}

function buildSectionPayload(section: FormSection, order: number) {
  const trimmedDesc = section.description.trim().slice(0, 500);
  return {
    id: toApiId(section.id),
    name: section.name.slice(0, 200),
    order,
    layout: { columns: 2 },
    ...(trimmedDesc ? { description: trimmedDesc } : {}),
    fields: section.fields.map((f, idx) => buildFieldPayload(f, idx + 1)),
  };
}

export function buildPayload(state: FormBuilderState) {
  const payload: Record<string, unknown> = {
    name: state.name.trim(),
    sections: state.sections.map((s, idx) => buildSectionPayload(s, idx + 1)),
  };
  if (state.description.trim()) {
    payload['description'] = state.description.trim();
  }
  return payload;
}

// ─── Error helpers ────────────────────────────────────────────────────────────

function extractErrorMessage(errorData: unknown, status?: number): string {
  if (status === 401 || status === 403) {
    return TASK_FORM_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 500) {
    return TASK_FORM_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }
  if (status === 400) {
    if (errorData && typeof errorData === 'object' && 'message' in errorData) {
      return (errorData as { message: string }).message;
    }
    return TASK_FORM_ERROR_MESSAGES.VALIDATION_ERROR;
  }
  if (errorData && typeof errorData === 'object' && 'message' in errorData) {
    return (errorData as { message: string }).message;
  }
  return TASK_FORM_ERROR_MESSAGES.DEFAULT_ERROR;
}

// ─── Public API ───────────────────────────────────────────────────────────────

export interface CreateTaskFormResult {
  success: boolean;
  data?: CreateTaskFormResponse;
  error?: string;
}

export async function createTaskForm(
  state: FormBuilderState
): Promise<CreateTaskFormResult> {
  try {
    const payload = buildPayload(state);
    const result = await apiService.postAPI(taskFormEndpoints.FORMS, payload);

    if (!result.error) {
      return { success: true, data: result.data as CreateTaskFormResponse };
    }

    if (result.isNetworkError) {
      return { success: false, error: TASK_FORM_ERROR_MESSAGES.NETWORK_ERROR };
    }

    return {
      success: false,
      error: extractErrorMessage(result.data, result.status),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      if (isNetworkError) {
        return {
          success: false,
          error: TASK_FORM_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }
      return {
        success: false,
        error: extractErrorMessage(
          error.response?.data,
          error.response?.status
        ),
      };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : TASK_FORM_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
}
